import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { ArrowLeft, RotateCcw, Flag, Play, Check, Eye, EyeOff, Ear, EarOff, SlidersHorizontal } from 'lucide-react';
import { tvKey } from './tvKeys';
import { popNav, popOk, LOGO_URL } from './tvAssets';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PrevEpIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" {...props}>
      <rect x="3.5" y="5" width="2.5" height="14" rx="1" />
      <path d="M18 4L6 12l12 8V4z" opacity="0.9" />
    </svg>
  );
}
function NextEpIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" stroke="none" {...props}>
      <path d="M6 4l12 8-12 8V4z" opacity="0.9" />
      <rect x="18" y="5" width="2.5" height="14" rx="1" />
    </svg>
  );
}

function Timeline({ currentTime, duration, formatTime, focused, markers = [] }) {
  const pct = duration ? Math.min(100, (currentTime / duration) * 100) : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
      <span style={{ fontSize: '1.5vw', flex: 'none', marginRight: '2.8vw', fontWeight: 600, fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }} data-testid="tvp-elapsed">{formatTime(currentTime)}</span>
      <div style={{ flex: 1, position: 'relative', height: '0.3vw', background: 'rgba(255,255,255,0.25)', borderRadius: '1vw' }} data-testid="tvp-timeline">
        <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${pct}%`, background: '#E9641A', borderRadius: '1vw' }} />
        {duration > 0 && markers.map(m => {
          const mp = (m / duration) * 100;
          if (mp <= 0 || mp >= 100) return null;
          return <div key={m} data-testid="tvp-ad-marker" style={{ position: 'absolute', top: '50%', transform: 'translate(-50%,-50%)', left: `${mp}%`, width: '0.6vw', height: '0.6vw', background: '#3B82F6', borderRadius: '50%', boxShadow: '0 0 0.3vw rgba(0,0,0,0.6)' }} />;
        })}
        <div style={{ position: 'absolute', top: '50%', transform: 'translate(-50%,-50%)', left: `${pct}%`,
          width: focused ? '1.1vw' : '0.8vw', height: focused ? '1.1vw' : '0.8vw', background: '#E9641A', borderRadius: '50%',
          boxShadow: focused ? '0 0 0.8vw rgba(233,100,26,0.9)' : 'none', transition: 'width 0.15s, height 0.15s' }} />
      </div>
      <span style={{ fontSize: '1.5vw', flex: 'none', marginLeft: '2.8vw', fontWeight: 600, fontFamily: "'Manrope', 'Avenir', Arial, sans-serif", color: 'rgba(255,255,255,0.7)' }} data-testid="tvp-remaining">-{formatTime(Math.max(0, duration - currentTime))}</span>
    </div>
  );
}

export function TvPlayerOverlay({
  content, episode, playing, currentTime, duration, adActive, videoError,
  onTogglePlay, onSeek, onRestart, onPrevEp, onNextEp, hasPrevEp, hasNextEp, onBack, onAdToggle,
  showSkipIntro, showNextEpBtn, onSkipIntro, showSkipRecap, onSkipRecap, onSeekCommit, hiddenRanges = [],
  selectedAudio, setSelectedAudio, selectedSub, setSelectedSub, filters, setFilters, adminFilters, pauseAd, formatTime,
  videoRef, mp4Url,
  onReport, reportOpen, adMarkers = [],
}) {
  const [scrub, setScrub] = useState(null); // seconds (10s grid) while trick-play scrubbing
  const paused = !playing && !adActive && !videoError && duration > 0 && scrub === null;
  const [phase, setPhase] = useState('controls'); // controls | dim | saver
  const [waking, setWaking] = useState(false);
  const [zone, setZone] = useState('main'); // top | main | pills
  const [topIdx, setTopIdx] = useState(0);
  const [pillIdx, setPillIdx] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [fIdx, setFIdx] = useState(0);
  const [flagToast, setFlagToast] = useState(false);
  const [, setThumbTick] = useState(0);
  const timers = useRef([]);
  const scrubRepeat = useRef(0);
  const scrubFromPause = useRef(false); // scrub opened from the pause overlay -> cancel returns to pause
  const thumbCache = useRef(new Map());
  const thumbVideoRef = useRef(null);
  const genId = useRef(0);
  const scrubOrigin = useRef(null); // position de lecture au moment d'ouvrir le scrub
  const impressionSent = useRef(false);
  const maxScrub = Math.max(0, Math.floor(Math.max(0, duration - 1) / 10) * 10);
  // Vignette masquée si le curseur est dans une scène filtrée OU si l'image réellement
  // affichée (frame sprite capturée à idx*interval, parfois bien avant le curseur) vient de la scène
  const spriteSrc = episode?.sprite_url || content?.sprite_url || null;
  const spriteMeta = (episode?.sprite_url ? episode?.sprite_meta : content?.sprite_meta) || null;
  const isHiddenBucket = useCallback((b) => {
    let ft = b + 0.1;
    if (spriteSrc && spriteMeta) {
      const iv = spriteMeta.interval || 10;
      const idx = Math.max(0, Math.min((spriteMeta.count || spriteMeta.cols * spriteMeta.rows) - 1, Math.floor(b / iv)));
      ft = idx * iv + 0.1;
    }
    return hiddenRanges.some(([s, e]) => (b + 0.1 >= s && b + 0.1 < e) || (ft >= s - 0.3 && ft < e + 0.3));
  }, [hiddenRanges, spriteSrc, spriteMeta]);

  // Planche de vignettes générée côté serveur (fiable sur TV) — prioritaire sur la capture canvas
  const spriteStyle = useCallback((b) => {
    const { interval, cols, rows, count } = spriteMeta;
    const idx = Math.max(0, Math.min((count || cols * rows) - 1, Math.floor(b / (interval || 10))));
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    return {
      width: '100%', height: '100%',
      backgroundImage: `url(${spriteSrc})`,
      backgroundSize: `${cols * 100}% ${rows * 100}%`,
      backgroundPosition: `${cols > 1 ? (col * 100) / (cols - 1) : 0}% ${rows > 1 ? (row * 100) / (rows - 1) : 0}%`,
      backgroundRepeat: 'no-repeat',
    };
  }, [spriteSrc, spriteMeta]);

  const scrubRef = useRef(null);
  useEffect(() => { scrubRef.current = scrub; }, [scrub]);
  // Garde : si quelque chose relance la lecture pendant le scrub, on re-pause aussitôt
  useEffect(() => {
    if (scrub === null) return;
    const v = videoRef?.current;
    if (!v) return;
    const guard = () => { if (scrubRef.current !== null) { try { v.pause(); } catch { /* ignore */ } } };
    guard();
    v.addEventListener('play', guard);
    v.addEventListener('playing', guard);
    return () => { v.removeEventListener('play', guard); v.removeEventListener('playing', guard); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrub === null, videoRef]);

  const confirmScrub = useCallback(() => {
    const v = videoRef?.current;
    scrubRef.current = null; // libère la garde anti-lecture avant de reprendre
    if (v && scrub !== null) {
      const from = scrubOrigin.current != null ? scrubOrigin.current : v.currentTime;
      v.muted = false; // le scrub coupe le son (muted) — on le rétablit à la reprise
      v.currentTime = scrub;
      // Anti-saut de pub : si le scrub franchit un marqueur pub, la pub se lance (pas de play ici)
      const adLaunched = onSeekCommit ? onSeekCommit(from, scrub) : false;
      if (!adLaunched) v.play().catch(() => {});
    }
    scrubOrigin.current = null;
    setScrub(null);
  }, [videoRef, scrub, onSeekCommit]);

  const cancelScrub = useCallback((resume = true) => {
    const v = videoRef?.current;
    scrubRef.current = null; // libère la garde anti-lecture avant de reprendre
    if (v) {
      // revient à la position d'origine (la vidéo de fond a suivi le scrub en direct)
      v.muted = false;
      if (scrubOrigin.current != null) { try { v.currentTime = scrubOrigin.current; } catch { /* ignore */ } }
      if (resume) v.play().catch(() => {});
      else { try { v.pause(); } catch { /* ignore */ } }
    }
    scrubOrigin.current = null;
    setScrub(null);
  }, [videoRef]);

  // Prévisualisation live : pendant le scrub, la vidéo principale (en pause, plein écran derrière)
  // se cale sur la position visée → on voit toujours la vraie scène du film
  useEffect(() => {
    if (scrub === null) return;
    const v = videoRef?.current;
    if (!v) return;
    const t = setTimeout(() => { try { v.muted = true; if (!v.paused) v.pause(); v.currentTime = scrub; } catch { /* ignore */ } }, 200);
    return () => clearTimeout(t);
  }, [scrub, videoRef]);

  // Reset thumbnail cache when the source changes (episode switch)
  useEffect(() => {
    thumbCache.current = new Map();
    const v = thumbVideoRef.current;
    if (v) { delete v.dataset.src; v.removeAttribute('src'); }
  }, [mp4Url]);

  // Generate trick-play thumbnails around the scrub position (hidden MP4 video + canvas)
  // Inutile quand une planche de sprites serveur existe (spriteSrc)
  useEffect(() => {
    if (scrub === null || !mp4Url || (spriteSrc && spriteMeta)) return;
    const id = ++genId.current;
    const t = setTimeout(async () => {
      const v = thumbVideoRef.current;
      if (!v) return;
      if (!v.dataset.src) {
        v.dataset.src = '1';
        v.crossOrigin = 'anonymous';
        v.muted = true;
        v.preload = 'auto';
        // Cache-buster: le CDN peut avoir mis en cache la vidéo SANS en-têtes CORS
        // (requête du lecteur principal) → la capture canvas échouerait
        v.src = mp4Url + (mp4Url.includes('?') ? '&' : '?') + 'tp=1';
        v.onerror = () => { delete v.dataset.src; v.removeAttribute('src'); };
      }
      const buckets = [];
      for (let off = -30; off <= 30; off += 10) {
        const b = scrub + off;
        if (b >= 0 && b <= maxScrub && !thumbCache.current.has(b) && !isHiddenBucket(b)) buckets.push(b);
      }
      buckets.sort((a, b) => Math.abs(a - scrub) - Math.abs(b - scrub));
      for (const b of buckets) {
        if (genId.current !== id) return;
        try {
          await new Promise((res, rej) => {
            const done = () => { v.removeEventListener('seeked', done); res(); };
            v.addEventListener('seeked', done);
            v.currentTime = b + 0.1;
            setTimeout(() => { v.removeEventListener('seeked', done); rej(new Error('timeout')); }, 8000);
          });
          if (genId.current !== id) return;
          // attend que la frame soit réellement décodée avant capture (TV lentes)
          await new Promise(res => requestAnimationFrame(() => setTimeout(res, 60)));
          if (genId.current !== id) return;
          const c = document.createElement('canvas');
          c.width = 320; c.height = 180;
          const ctx = c.getContext('2d');
          ctx.drawImage(v, 0, 0, 320, 180);
          // rejette les frames noires (pipeline vidéo pas prêt) : la tuile garde l'horodatage à la place
          const px = ctx.getImageData(0, 0, 320, 180).data;
          let lum = 0;
          for (let i = 0; i < px.length; i += 7680) lum += px[i] + px[i + 1] + px[i + 2];
          if (lum < 60) continue;
          thumbCache.current.set(b, c.toDataURL('image/jpeg', 0.6));
          setThumbTick(x => x + 1);
        } catch { /* thumb stays as placeholder */ }
      }
    }, 250);
    return () => clearTimeout(t);
  }, [scrub, maxScrub, mp4Url]);

  const arm = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [
      setTimeout(() => setPhase('dim'), 5000),
      setTimeout(() => setPhase('saver'), 15000),
    ];
    setPhase('controls');
  }, []);

  useEffect(() => {
    if (paused) { setZone('main'); setTopIdx(0); setPillIdx(0); arm(); }
    else { timers.current.forEach(clearTimeout); setPhase('controls'); setWaking(false); setShowFilters(false); }
    return () => timers.current.forEach(clearTimeout);
  }, [paused, arm]);

  useEffect(() => {
    if (phase === 'saver' && pauseAd && !impressionSent.current) {
      impressionSent.current = true;
      axios.post(`${API}/ads/${pauseAd.id}/impression`).catch(() => {});
    }
  }, [phase, pauseAd]);

  const wake = useCallback(() => {
    setWaking(true);
    setZone('main');
    setTimeout(() => { setWaking(false); arm(); }, 2000);
  }, [arm]);

  const topBtns = [
    { id: 'back', label: 'Retour' },
    { id: 'restart', label: 'Reprendre du début' },
    ...(hasPrevEp ? [{ id: 'prev', label: 'Épisode précédent' }] : []),
    ...(hasNextEp ? [{ id: 'next', label: 'Épisode suivant' }] : []),
    { id: 'flag', label: 'Signaler un problème' },
  ];

  const lang = content?.language || 'Français';
  const audioTracks = content?.audio_tracks || [];
  const subs = content?.subtitles || [];
  const burned = content?.burned_subtitles_lang || '';
  const pills = [
    { kind: 'audio', value: '__original__', label: lang.toLowerCase(), checked: selectedAudio === '__original__' },
    ...audioTracks.map(t => ({ kind: 'audio', value: t.language, label: t.language.toLowerCase(), checked: selectedAudio === t.language })),
    ...(burned && subs.length === 0 ? [{ kind: 'burned', label: `sous-titres ${burned.toLowerCase()} intégrés`, checked: true }] : []),
    ...subs.map(s => ({ kind: 'sub', value: s.language, label: `sous-titres ${s.language.toLowerCase()}`, checked: selectedSub === s.language })),
    ...(adminFilters.length ? [{ kind: 'filters', label: 'Filtres', checked: false }] : []),
  ];

  const doTop = (id) => {
    if (id === 'back') onBack();
    else if (id === 'restart') onRestart();
    else if (id === 'prev') onPrevEp();
    else if (id === 'next') onNextEp();
    else if (id === 'flag') { if (onReport) onReport(); else { setFlagToast(true); setTimeout(() => setFlagToast(false), 2500); } }
  };

  const doPill = (p) => {
    if (p.kind === 'audio') setSelectedAudio(p.value);
    else if (p.kind === 'sub') setSelectedSub(selectedSub === p.value ? 'Désactivés' : p.value);
    else if (p.kind === 'filters') { setFIdx(0); setShowFilters(true); }
  };

  useEffect(() => {
    const onKey = (e) => {
      if (reportOpen) return; // report modal handles keys
      const k = tvKey(e);
      if (!k) return;
      e.preventDefault();
      e.stopPropagation();
      if (adActive) {
        if (k === 'back') onBack();
        else if (k === 'ok') onAdToggle();
        return;
      }
      if (scrub !== null) {
        if (k === 'left' || k === 'right') {
          scrubRepeat.current = e.repeat ? scrubRepeat.current + 1 : 0;
          const step = scrubRepeat.current > 12 ? 60 : scrubRepeat.current > 4 ? 30 : 10;
          setScrub(s => Math.max(0, Math.min(maxScrub, s + (k === 'left' ? -step : step))));
        } else if (k === 'ok') { confirmScrub(); }
        else if (k === 'back' || k === 'up') { cancelScrub(true); }  // retour/haut : position d'origine + reprise
        else if (k === 'down') { cancelScrub(false); }               // bas : position d'origine, reste en pause
        return;
      }
      if (!paused) {
        if (k === 'back') { onBack(); return; }
        if (k === 'left' || k === 'right') {
          // Netflix-style trick play: pause and open the thumbnail scrubber
          const v = videoRef?.current;
          if (v && duration > 0) {
            v.pause();
            v.muted = true;
            scrubRepeat.current = 0;
            scrubFromPause.current = false;
            scrubOrigin.current = v.currentTime;
            setScrub(Math.max(0, Math.min(maxScrub, Math.round((currentTime + (k === 'left' ? -10 : 10)) / 10) * 10)));
          }
          return;
        }
        if (k === 'ok') {
          // OK triggers the visible "Passer l'intro" / "Passer le récap" / "Épisode suivant" button directly
          if (showSkipIntro) { popOk(); onSkipIntro(); return; }
          if (showSkipRecap) { popOk(); onSkipRecap(); return; }
          if (showNextEpBtn) { popOk(); onNextEp(); return; }
        }
        if (k === 'ok' || k === 'down') { onTogglePlay(); }
        return;
      }
      if (waking) return;
      if (phase === 'saver') { wake(); return; }
      arm();
      if (showFilters) {
        if (k === 'back' || k === 'left') { setShowFilters(false); return; }
        if (k === 'up') { if (fIdx > 0) popNav(); setFIdx(i => Math.max(0, i - 1)); return; }
        if (k === 'down') { if (fIdx < adminFilters.length - 1) popNav(); setFIdx(i => Math.min(adminFilters.length - 1, i + 1)); return; }
        if (k === 'ok') { popOk(); const f = adminFilters[fIdx]; if (f) setFilters(prev => ({ ...prev, [f.id]: !prev[f.id] })); }
        return;
      }
      if (k === 'back') { onBack(); return; }
      if (zone === 'top') {
        if (k === 'left') { if (topIdx > 0) { popNav(); setTopIdx(i => i - 1); } }
        else if (k === 'right') { if (topIdx < topBtns.length - 1) { popNav(); setTopIdx(i => i + 1); } }
        else if (k === 'down') { popNav(); setZone('main'); }
        else if (k === 'ok') { popOk(); doTop(topBtns[topIdx].id); }
      } else if (zone === 'main') {
        if (k === 'up') { popNav(); setZone('top'); setTopIdx(0); }
        else if (k === 'down') { if (pills.length) { popNav(); setZone('pills'); setPillIdx(0); } }
        else if (k === 'left' || k === 'right') {
          // From pause: open the thumbnail scrubber instead of seeking/resuming
          if (duration > 0) {
            scrubRepeat.current = 0;
            scrubFromPause.current = true;
            scrubOrigin.current = videoRef?.current ? videoRef.current.currentTime : currentTime;
            setScrub(Math.max(0, Math.min(maxScrub, Math.round((currentTime + (k === 'left' ? -10 : 10)) / 10) * 10)));
          }
        }
        else if (k === 'ok') { onTogglePlay(); }
      } else {
        if (k === 'up') { popNav(); setZone('main'); }
        else if (k === 'left') { if (pillIdx > 0) { popNav(); setPillIdx(i => i - 1); } }
        else if (k === 'right') { if (pillIdx < pills.length - 1) { popNav(); setPillIdx(i => i + 1); } }
        else if (k === 'ok') { popOk(); doPill(pills[pillIdx]); }
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  });

  const iconStyle = (fc) => ({
    width: '3.2vw', height: '3.2vw', cursor: 'pointer',
    color: fc ? '#E9641A' : 'rgba(255,255,255,0.85)',
    transition: 'color 0.15s',
    filter: fc ? 'drop-shadow(0 0 0.5vw rgba(233,100,26,0.8))' : 'none',
  });

  if (videoError) {
    return (
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' }} data-testid="tvp-error">
        <p style={{ color: '#f87171', fontSize: '1.3vw', fontWeight: 700, marginBottom: '1vw' }}>{videoError}</p>
        <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1vw' }}>Appuyez sur Retour</p>
      </div>
    );
  }

  const leftBtns = topBtns.filter(b => b.id !== 'flag');
  const flagIdx = topBtns.length - 1;
  const epLine = episode ? `S${episode.season || 1} : E${episode.episode || '?'}${episode.title ? ` « ${episode.title} »` : ''}` : '';
  const isOriginal = content?.category === 'originals' || (content?.categories || []).includes('originals');

  return (
    <>
      {/* Hidden video used to capture trick-play thumbnails */}
      <video ref={thumbVideoRef} muted playsInline preload="auto" style={{ display: 'none' }} />

      {/* Netflix-style trick-play scrubber (left/right during playback) */}
      {scrub !== null && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 45, color: '#fff', background: 'linear-gradient(0deg, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.45) 32%, transparent 55%)' }} data-testid="tvp-scrub">
          {/* Filmstrip */}
          <div style={{ position: 'absolute', bottom: '17vh', left: 0, right: 0, height: '14vw', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', left: '50%', top: 0, bottom: 0, width: 0,
              transform: `translateX(${-(scrub / 10) * 23}vw)`, transition: 'transform 0.18s ease-out' }}>
              {Array.from({ length: 9 }, (_, i) => scrub + (i - 4) * 10)
                .filter(b => b >= 0 && b <= maxScrub)
                .map(b => {
                  const c = b === scrub;
                  const hidden = isHiddenBucket(b);
                  const img = hidden ? null : thumbCache.current.get(b);
                  return (
                    <div key={b}
                      style={{ position: 'absolute', left: `${(b / 10) * 23}vw`, top: '50%', transform: `translate(-50%, -50%) scale(${c ? 1.16 : 1})`,
                        width: '21vw', height: '11.8vw', borderRadius: '0.5vw', overflow: 'hidden', background: '#0d0d14',
                        border: c ? '0.28vw solid #4FA3FF' : '0.12vw solid rgba(255,255,255,0.25)',
                        boxShadow: c ? '0 0 1.8vw rgba(79,163,255,0.55)' : '0 0.3vw 1vw rgba(0,0,0,0.6)',
                        transition: 'transform 0.18s, border-color 0.18s', zIndex: c ? 2 : 1 }}
                      data-testid={c ? 'tvp-scrub-thumb-center' : `tvp-scrub-thumb-${b}`}>
                      {hidden
                        ? <div style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)' }} data-testid={`tvp-scrub-hidden-${b}`}>
                            <EyeOff style={{ width: '2.2vw', height: '2.2vw', marginBottom: '0.4vw' }} />
                            <span style={{ fontSize: '0.95vw', fontWeight: 700, fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }}>Scène filtrée</span>
                          </div>
                        : spriteSrc && spriteMeta
                        ? <div style={spriteStyle(b)} data-testid={`tvp-scrub-sprite-${b}`} />
                        : img
                        ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#14141d',
                            color: '#fff', fontSize: '1.3vw', fontWeight: 800, textShadow: '0 0.1vw 0.6vw rgba(0,0,0,0.9)',
                            fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }}>{formatTime(b)}</div>}
                      {c && (
                        <div style={{ position: 'absolute', bottom: '0.4vw', left: 0, right: 0, textAlign: 'center', fontSize: '1vw', fontWeight: 800, textShadow: '0 0.1vw 0.4vw rgba(0,0,0,0.9)' }} data-testid="tvp-scrub-time">
                          {formatTime(b)}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
          {/* Timeline */}
          <div style={{ position: 'absolute', left: '4vw', right: '4vw', bottom: '9vh', display: 'flex', alignItems: 'center' }}>
            <span style={{ fontSize: '1.1vw', marginRight: '1.4vw', fontFamily: "'Manrope', 'Avenir', Arial, sans-serif" }} data-testid="tvp-scrub-elapsed">{formatTime(scrub)}</span>
            <div style={{ flex: 1, position: 'relative', height: '0.3vw', background: 'rgba(255,255,255,0.25)', borderRadius: '1vw' }}>
              <div style={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${duration ? Math.min(100, (scrub / duration) * 100) : 0}%`, background: '#E9641A', borderRadius: '1vw' }} />
              <div style={{ position: 'absolute', top: '50%', transform: 'translate(-50%,-50%)', left: `${duration ? Math.min(100, (scrub / duration) * 100) : 0}%`, width: '1vw', height: '1vw', background: '#E9641A', borderRadius: '50%', boxShadow: '0 0 0.8vw rgba(233,100,26,0.9)' }} />
            </div>
            <span style={{ fontSize: '1.1vw', marginLeft: '1.4vw', fontFamily: "'Manrope', 'Avenir', Arial, sans-serif", color: 'rgba(255,255,255,0.75)' }} data-testid="tvp-scrub-total">{formatTime(duration)}</span>
          </div>
          <p style={{ position: 'absolute', bottom: '4.5vh', left: 0, right: 0, textAlign: 'center', fontSize: '0.85vw', color: 'rgba(255,255,255,0.5)' }}>
            OK : reprendre ici · Retour : annuler · Maintenez ←/→ pour aller plus vite
          </p>
        </div>
      )}

      {paused && (
        <div data-testid="tv-player-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 40, color: '#fff', overflow: 'hidden' }}>
          {/* Scrims */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.65) 0%, transparent 22%), linear-gradient(0deg, rgba(0,0,0,0.72) 0%, transparent 32%)' }} />
          {/* Dim layer after 5s */}
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.25)', opacity: phase !== 'controls' ? 1 : 0, transition: 'opacity 1.5s ease' }} data-testid="tvp-dim" />
          {/* Dim big title */}
          <div style={{ position: 'absolute', left: '5vw', top: '30%', maxWidth: '44vw', opacity: phase !== 'controls' ? 1 : 0, transition: 'opacity 1.5s ease', pointerEvents: 'none' }} data-testid="tvp-dim-title">
            <img src={LOGO_URL} alt="Maya+" style={{ height: '2.2vw', marginBottom: '0.6vw', objectFit: 'contain', objectPosition: 'left' }} data-testid="tvp-dim-logo" />
            {content?.logo_url
              ? <img src={content.logo_url} alt="" style={{ maxHeight: '9vw', maxWidth: '30vw', objectFit: 'contain' }} />
              : <h1 style={{ fontSize: '3.4vw', fontWeight: 900, lineHeight: 1.05 }}>{content?.title}</h1>}
            {epLine && <p style={{ fontSize: '1.25vw', marginTop: '0.7vw', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{epLine}</p>}
          </div>

          {/* Top-left buttons : espacement fixe, le label absolu ne déplace jamais les icônes */}
          <div style={{ position: 'absolute', top: '7vh', left: '5vw', display: 'flex', alignItems: 'flex-start' }}>
            {leftBtns.map((b, i) => {
              const fc = zone === 'top' && topIdx === i;
              return (
                <div key={b.id} style={{ textAlign: 'center', position: 'relative', width: '3.2vw', marginRight: i < leftBtns.length - 1 ? '8vw' : 0 }} onClick={() => doTop(b.id)} data-testid={`tvp-${b.id}`}>
                  {b.id === 'back' && <ArrowLeft style={iconStyle(fc)} />}
                  {b.id === 'restart' && <RotateCcw style={iconStyle(fc)} />}
                  {b.id === 'prev' && <PrevEpIcon style={iconStyle(fc)} />}
                  {b.id === 'next' && <NextEpIcon style={iconStyle(fc)} />}
                  {fc && <p style={{ position: 'absolute', top: '3.6vw', left: '50%', transform: 'translateX(-50%)', fontSize: '1vw', color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap', fontWeight: 600 }}>{b.label}</p>}
                </div>
              );
            })}
          </div>

          {/* Top-right: title + episode + flag */}
          <div style={{ position: 'absolute', top: '7vh', right: '5vw', display: 'flex', alignItems: 'flex-start', maxWidth: '46vw' }}>
            <div style={{ textAlign: 'right', overflow: 'hidden' }}>
              {isOriginal && (
                <img src={LOGO_URL} alt="Maya+" style={{ height: '1.3vw', marginBottom: '0.6vw', marginLeft: 'auto', display: 'block', objectFit: 'contain',
                  opacity: phase !== 'controls' ? 0 : 1, transition: 'opacity 1.5s ease' }} data-testid="tvp-original-logo" />
              )}
              <p style={{ fontSize: '1.35vw', fontWeight: 800, maxWidth: '30vw', lineHeight: 1.25 }} data-testid="tvp-title">{content?.title}</p>
              {epLine && <p style={{ fontSize: '0.95vw', color: 'rgba(255,255,255,0.75)', marginTop: '0.25vw' }} data-testid="tvp-episode-line">{epLine}</p>}
            </div>
            <div onClick={() => doTop('flag')} data-testid="tvp-flag" style={{ textAlign: 'center', flex: 'none', position: 'relative', width: '2.4vw', marginLeft: '7vw' }}>
              <Flag style={{ ...iconStyle(zone === 'top' && topIdx === flagIdx), width: '2.4vw', height: '2.4vw' }} />
              {zone === 'top' && topIdx === flagIdx && <p style={{ position: 'absolute', top: '2.8vw', left: '50%', transform: 'translateX(-50%)', fontSize: '0.75vw', color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap' }}>Signaler</p>}
            </div>
          </div>

          {flagToast && (
            <div style={{ position: 'absolute', top: '12vh', right: '3vw', padding: '0.5vw 1.2vw', background: 'rgba(0,0,0,0.8)', borderRadius: '0.5vw', fontSize: '0.95vw' }} data-testid="tvp-flag-toast">
              Signalement envoyé
            </div>
          )}

          {/* Bottom: play + timeline + pills */}
          <div style={{ position: 'absolute', left: '7vw', right: '7vw', bottom: '4vh' }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.6vw' }}>
              <div onClick={onTogglePlay} data-testid="tvp-play"
                style={{ width: '4.6vw', height: '4.6vw', borderRadius: '50%', flex: 'none', marginRight: '4vw', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: zone === 'main' ? '#fff' : 'rgba(255,255,255,0.85)', cursor: 'pointer',
                  transition: 'background-color 0.15s, box-shadow 0.15s',
                  boxShadow: zone === 'main' ? '0 0 1.4vw rgba(233,100,26,0.8)' : 'none' }}>
                <Play style={{ width: '2.2vw', height: '2.2vw', color: '#000', fill: '#000', marginLeft: '0.2vw' }} />
              </div>
              <Timeline currentTime={currentTime} duration={duration} formatTime={formatTime} focused={zone === 'main'} markers={adMarkers} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap' }} data-testid="tvp-pills">
              {pills.map((p, i) => {
                const fc = zone === 'pills' && pillIdx === i;
                return (
                  <div key={`${p.kind}-${i}`} onClick={() => doPill(p)} data-testid={`tvp-pill-${p.kind}-${i}`}
                    style={{ display: 'flex', alignItems: 'center', margin: '0 0.9vw 0.6vw', padding: '0.75vw 1.8vw', borderRadius: '2.4vw', fontSize: '1.2vw', fontWeight: 600, cursor: 'pointer',
                      background: fc ? '#fff' : 'rgba(45,45,45,0.8)', color: fc ? '#000' : '#fff', transition: 'background-color 0.15s, color 0.15s' }}>
                    {p.checked && <Check style={{ width: '1.25vw', height: '1.25vw', marginRight: '0.65vw' }} />}
                    {p.kind === 'filters' && <SlidersHorizontal style={{ width: '1.25vw', height: '1.25vw', marginRight: '0.65vw' }} />}
                    {p.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* TV Filters panel */}
          {showFilters && (
            <div style={{ position: 'absolute', bottom: '15vh', left: '50%', transform: 'translateX(-50%)', background: 'rgba(18,18,18,0.96)', borderRadius: '0.8vw', padding: '1.2vw 1.6vw', minWidth: '24vw', border: '1px solid rgba(255,255,255,0.1)' }} data-testid="tvp-filters-panel">
              <p style={{ fontSize: '1.1vw', fontWeight: 800, marginBottom: '0.8vw' }}>Vos filtres :</p>
              {adminFilters.map((f, i) => {
                const active = filters[f.id];
                const fc = i === fIdx;
                const isMute = f.action === 'mute';
                const FIcon = isMute ? (active ? EarOff : Ear) : (active ? EyeOff : Eye);
                return (
                  <div key={f.id} data-testid={`tvp-filter-${f.id}`}
                    style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5vw 0.8vw', borderRadius: '0.4vw',
                      background: fc ? '#E9641A' : 'transparent' }}>
                    <span style={{ fontSize: '1vw', fontWeight: 700, marginRight: '2.5vw', textDecoration: active ? 'line-through' : 'none', opacity: active ? 0.65 : 1, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{f.name}</span>
                    <FIcon style={{ width: '1.1vw', height: '1.1vw', flex: 'none' }} />
                  </div>
                );
              })}
              <p style={{ fontSize: '0.78vw', color: 'rgba(255,255,255,0.4)', marginTop: '0.7vw' }}>OK : activer/désactiver · Retour : fermer</p>
            </div>
          )}

          {/* Pause screensaver: film banner slide OR static ad for ad plans */}
          {(phase === 'saver' || waking) && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 6, background: '#000', opacity: waking ? 0 : 1, transition: 'opacity 2s ease', animation: waking ? 'none' : 'tvpFadeIn 1.5s ease' }} data-testid="tvp-saver">
              {pauseAd?.image_url ? (
                <>
                  <img src={pauseAd.image_url} alt="" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover' }} data-testid="tvp-pause-ad" />
                  <div style={{ position: 'absolute', top: '3.5vh', left: '3vw', padding: '0.3vw 1vw', background: 'rgba(0,0,0,0.55)', borderRadius: '0.4vw', fontSize: '0.9vw', fontWeight: 700 }}>Publicité</div>
                  <div style={{ position: 'absolute', right: '3vw', bottom: '5vh', display: 'flex', alignItems: 'center', background: 'rgba(0,0,0,0.65)', padding: '0.8vw 1.2vw', borderRadius: '0.8vw' }} data-testid="tvp-pause-ad-film-card">
                    <img src={content?.poster_url || content?.thumbnail_url} alt="" style={{ width: '6vw', height: '9vw', objectFit: 'cover', borderRadius: '0.4vw', marginRight: '1vw' }} />
                    <div>
                      <p style={{ fontSize: '0.85vw', color: 'rgba(255,255,255,0.6)' }}>En pause</p>
                      <p style={{ fontSize: '1.1vw', fontWeight: 800, maxWidth: '16vw' }}>{content?.title}</p>
                      {epLine && <p style={{ fontSize: '0.85vw', color: 'rgba(255,255,255,0.7)' }}>{epLine}</p>}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <img src={content?.banner_url || content?.thumbnail_url} alt="" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%', objectFit: 'cover', animation: 'tvpSlide 22s ease-in-out infinite alternate' }} data-testid="tvp-saver-banner" />
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 45%), linear-gradient(90deg, rgba(0,0,0,0.55) 0%, transparent 45%)' }} />
                  <div style={{ position: 'absolute', left: '4vw', bottom: '7vh', maxWidth: '44vw' }}>
                    <img src={LOGO_URL} alt="Maya+" style={{ height: '2vw', marginBottom: '0.6vw', objectFit: 'contain', objectPosition: 'left' }} data-testid="tvp-saver-logo" />
                    {content?.logo_url
                      ? <img src={content.logo_url} alt="" style={{ maxHeight: '8vw', maxWidth: '28vw', objectFit: 'contain' }} />
                      : <h1 style={{ fontSize: '3vw', fontWeight: 900, lineHeight: 1.05 }}>{content?.title}</h1>}
                    {epLine && <p style={{ fontSize: '1.15vw', marginTop: '0.6vw', color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>{epLine}</p>}
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
